<template>
  <UIDialog
    :is-open
    :z-index
    :title
    :unclosable="isLoading"
    @close="handleClose"
  >
    <div class="text-center">
      <div class="text-base sm:text-sm">
        <slot v-if="isLoading" name="loading"
        >Importing and validating. This can take a few minutes</slot
        >
        <slot v-else name="info">
          You can use a spreadsheet to import new items. The spreadsheet file must
          be CSV.
        </slot>
        <slot v-if="!isLoading" name="example">
          An example format is available via the link below.
        </slot>
      </div>
    </div>
    <UIButton
      v-if="!isLoading"
      label="Download template"
      :leading="RectangleGroupIcon"
      variant="secondary"
      fill="light"
      class="mx-auto"
      @click="downloadTemplate"
    />
    <template #footer>
      <UIButtonsGroup class="justify-center">
        <UIButtonClose :disabled="isLoading" @click="handleClose" />
        <UIButtonSave
          label="Select a file"
          type="button"
          :leading="DocumentArrowDownIcon"
          :disabled="isLoading"
          :loading="isLoading"
          @click="processFile"
        />
      </UIButtonsGroup>
    </template>
  </UIDialog>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { parse } from 'papaparse'
import { snakeCase } from 'lodash'

import { GridColumn, ModalClass } from '@types'
import { ImportDialogItem, ImportDialogStep } from './utils/types'
import { validateHeadings } from './utils/business-logic'

import { CSV_SEPARATOR } from '@/const/common'
import { useNotifications } from '@/plugins/notification'

import {
  RectangleGroupIcon,
  DocumentArrowDownIcon,
} from '@heroicons/vue/24/outline'
import { UIDialog } from '@ui/modals'
import { UIButton, UIButtonClose, UIButtonSave, UIButtonsGroup } from '@ui/buttons'

type Props = {
  title: string
  modal: ModalClass<any>
  columns?: GridColumn[]
  headersExample: string
  legacyHeaders?: string[]
}

type Emits = {
  'import-start': []
  'import-step': [data: ImportDialogStep]
  'import-complete': [data: ImportDialogItem[]]
}

const { error } = useNotifications()

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

defineOptions({ name: 'UIImportDialog' })

const isLoading = ref(false)

const isOpen = computed(() => props.modal.isOpened)
const zIndex = computed(() => props.modal.zIndex)

const downloadTemplate = () => {
  const csvContent = props.headersExample
    ? `data:text/csv;charset=utf-8,${props.headersExample}`
    : ''
  const encodedUri = encodeURI(csvContent)
  const link = document.createElement('a')
  link.setAttribute('href', encodedUri)
  const fileName = `${snakeCase(props.title.toLowerCase())}_template.csv`
  link.setAttribute('download', fileName)
  document.body.appendChild(link)
  link.click()
}

const handleClose = () => {
  if (isLoading.value) return
  props.modal.close()
}

const processFile = () => {
  const input = document.createElement('input')
  input.type = 'file'
  input.accept = '.csv'
  const cols: Record<string, any> = {}
  props.columns?.forEach(col => {
    cols[col.name] = {
      isDate: col.filter?.isDate,
      name: col.name,
    }
  })
  input.addEventListener('change', () => {
    const file = input.files?.[0]
    if (!file || file.type !== 'text/csv') {
      error({
        message: 'Wrong file type',
      })
      return
    }
    emit('import-start')
    isLoading.value = true
    const result: ImportDialogItem[] = []
    let abortParse = false
    let index = 1
    parse(file, {
      header: true,
      delimiter: CSV_SEPARATOR,
      skipEmptyLines: true,
      transform: (value: string) => value.trim(),
      transformHeader: props.columns?.length
        ? function (header: string): string {
          return cols[header]?.name ?? header
        }
        : undefined,
      beforeFirstChunk: function (chunk) {
        const rows = chunk.split(/\r\n|\r|\n/)
        const headings = rows[0].toLowerCase()
        if (
          props.headersExample &&
          !validateHeadings(headings, props.headersExample, props.legacyHeaders)
        ) {
          error({
            message: 'Wrong file format. Please download the template',
          })
          abortParse = true
          return
        }

        const dateIndex = headings.split(',').indexOf('date')
        if (dateIndex !== -1) {
          for (let i = 1; i < rows.length; i++) {
            const row = rows[i].match(/(".*?"|[^",\s]+)(?=\s*,|\s*$)/g)
            const date = row?.[dateIndex]?.trim()

            if (date && !/^\d{4}-\d{2}-\d{2}$/.test(date)) {
              error({
                message:
                  'Wrong date format in file. Make sure all dates are in ISO format (YYYY-MM-DD)',
              })
              abortParse = true
              return
            }
          }
        }

        rows[0] = headings
        return rows.join('\r\n')
      },
      step: ({ data, errors, meta }, parser) => {
        const dataIsEmpty = Object.values(data as object).every(value => !value)

        if (abortParse || dataIsEmpty) {
          abortParse && parser.abort()
          return
        }

        index++
        if (errors.length) {
          error({
            message: `Error in row ${index}: ${errors
              .map(err => err.message)
              .join(', ')} (it will not be included in the result)`,
          })
        } else if (meta.aborted || typeof data !== 'object') {
          error({
            message: `The row ${index} will not be included in the result`,
          })
        } else {
          for (const property in data as ImportDialogItem) {
            if ((data as ImportDialogItem)[property] instanceof Error) {
              error({
                message: 'Wrong date format in file',
                description: ((data as ImportDialogItem)[property] as Error)
                  .message,
              })
              parser.abort()
            }
          }

          emit('import-step', {
            data: data as ImportDialogItem,
            index,
            callback: (item: ImportDialogItem) => {
              result.push(item)
            },
          })
        }
      },
      complete: function () {
        isLoading.value = false
        props.modal?.close()
        emit('import-complete', result)
      },
    })
  })
  input.classList.add('hidden')
  document.body.appendChild(input)
  input.click()
}
</script>
